
import { detectLocation, DetectedLocation, DetectedMultipleLocations } from './mappings/LocationMapping';
import { detectTravelConstraints } from './mappings/TravelDistanceMapping';
import { detectPriceConstraints } from './filters/PriceFilter';
import { detectFacilities, DetectedFacilities } from './filters/FacilitiesFilter';
import { handleCoastaBravaSearch } from './strategies/CoastaBravaSearchStrategy';
import { handleTravelConstraintSearch } from './strategies/TravelConstraintSearchStrategy';
import { handleStandardSearch } from './strategies/StandardSearchStrategy';

export const logDetectionSteps = (
  detectedLocation: DetectedLocation | DetectedMultipleLocations | null,
  priceConstraints: any,
  detectedFacilities: DetectedFacilities | null
) => {
  console.log('🔍 === DETECTION SUMMARY ===');
  
  if (detectedLocation) {
    if ('locations' in detectedLocation) {
      console.log('📍 Multiple locations detected:', detectedLocation.type);
      detectedLocation.locations.forEach((loc, i) => {
        console.log(`  ${i + 1}. ${loc.type}: ${JSON.stringify(loc.info)}`);
      });
    } else {
      console.log('📍 Location detected:', detectedLocation.type, JSON.stringify(detectedLocation.info));
    }
  } else {
    console.log('📍 No location detected');
  }
  
  if (priceConstraints) {
    console.log('💰 Price constraints:', priceConstraints);
  }
  
  if (detectedFacilities) {
    console.log('🏨 Facilities detected:', detectedFacilities);
  }
};

export const performBasicSearch = async (query: string) => {
  try {
    console.log('🔍 === PERFORMING STRICT HIERARCHICAL SEARCH ===');
    console.log('Original query:', `"${query}"`);
    
    const searchTerms = query.toLowerCase().split(' ');
    console.log('Split search terms:', searchTerms);

    // ======= STEP 1-4: DETECTION PHASE =======
    const detectedLocation = detectLocation(searchTerms);
    const priceConstraints = detectPriceConstraints(searchTerms);
    const detectedFacilities = detectFacilities(searchTerms);
    const travelConstraints = detectTravelConstraints(searchTerms);
    
    console.log('🔍 === DETECTION RESULTS ===');
    console.log('🎯 Location detected:', JSON.stringify(detectedLocation, null, 2));
    console.log('💰 Price constraints detected:', JSON.stringify(priceConstraints, null, 2));
    console.log('🏨 Facilities detected:', JSON.stringify(detectedFacilities, null, 2));
    console.log('🛫 Travel constraints detected:', JSON.stringify(travelConstraints, null, 2));
    
    // CRITICAL DEBUG: Check if KOS is being detected correctly
    if (query.toLowerCase().includes('kos')) {
      console.log('🚨 === KOS DEBUG MODE ===');
      console.log('🚨 Query contains "kos", location should be detected as Greece/Dodecanese/Kos');
      console.log('🚨 Actually detected location:', detectedLocation);
      console.log('🚨 Expected: {type: "city", info: {country: "Griekenland", region: "Dodecanese", city: "Kos"}}');
    }
    
    logDetectionSteps(detectedLocation, priceConstraints, detectedFacilities);
    
    if (travelConstraints) {
      console.log('🛫 === TRAVEL CONSTRAINTS DETECTED ===');
      console.log('Travel constraints:', travelConstraints);
    }

    // Check for experience/travel type terms (rondreis, stedentrip, etc.)
    const hasExperienceTerms = searchTerms.some(term => 
      term.includes('rondreis') || term.includes('stedentrip') || term.includes('safari') || 
      term.includes('cruise') || term.includes('wintersport') || term.includes('wellness') ||
      term.includes('cultureel') || term.includes('natuur') || term.includes('avontuur')
    );

    // Check for accommodation terms
    const hasAccommodationTerms = searchTerms.some(term => 
      term.includes('huis') || term.includes('appartement') || term.includes('hotel') || 
      term.includes('camping') || term.includes('resort') || term.includes('vakantie')
    );

    // SPECIAL HANDLING: Location + Experience (like "rondreis thailand") - NOW WITH STRICT COUNTRY FILTERING
    if (detectedLocation && hasExperienceTerms && !hasAccommodationTerms) {
      console.log('🎯 === LOCATION + EXPERIENCE MODE: STRICT country filtering for travel experiences ===');
      console.log('Detected location + experience combo - applying STRICT country filter');
      
      // Handle multiple locations differently  
      if ('locations' in detectedLocation) {
        console.log('Multiple locations detected for experience search - not yet supported');
        return [];
      }
      
      return await handleLocationExperienceSearch(searchTerms, detectedLocation, priceConstraints, detectedFacilities, travelConstraints, query);
    }

    // STRICT FILTERING: If we have a location + accommodation, apply both strictly
    if (detectedLocation && hasAccommodationTerms) {
      console.log('🎯 === STRICT SEARCH MODE: Location + Accommodation detected ===');
      console.log('Will apply STRICT filtering - no broad search fallback');
      
      // Handle multiple locations differently
      if ('locations' in detectedLocation) {
        return await handleMultipleLocationSearch(detectedLocation, priceConstraints, detectedFacilities, travelConstraints, query);
      }
      
      return await handleStandardSearch(searchTerms, detectedLocation, priceConstraints, detectedFacilities, travelConstraints, query);
    }

    // If we have travel constraints but no location, use travel constraints as primary filter
    if (travelConstraints && !detectedLocation) {
      console.log('🛫 Using travel constraints as primary filter (no location detected)');
      return await handleTravelConstraintSearch(travelConstraints, priceConstraints, detectedFacilities, query);
    }
    
    if (!detectedLocation && !travelConstraints) {
      console.log('❌ No location or travel constraints detected - cannot proceed with basic search');
      return [];
    }

    // Special handling for Costa Brava searches to get more results
    if (detectedLocation && 'info' in detectedLocation && detectedLocation?.type === 'region' && detectedLocation.info.region === 'Costa Brava') {
      return await handleCoastaBravaSearch(detectedLocation, priceConstraints, detectedFacilities, travelConstraints, query);
    }

    // Handle multiple locations
    if (detectedLocation && 'locations' in detectedLocation) {
      return await handleMultipleLocationSearch(detectedLocation, priceConstraints, detectedFacilities, travelConstraints, query);
    }

    // Standard search flow with STRICT filtering  
    return await handleStandardSearch(searchTerms, detectedLocation as DetectedLocation, priceConstraints, detectedFacilities, travelConstraints, query);
    
  } catch (error: any) {
    console.error('💥 Strict hierarchical search error:', error);
    throw error;
  }
};

// Updated function to handle Location + Experience searches with STRICT country filtering
const handleLocationExperienceSearch = async (
  searchTerms: string[],
  detectedLocation: DetectedLocation,
  priceConstraints: any,
  detectedFacilities: any,
  travelConstraints: any,
  query: string
) => {
  console.log('🌍 === LOCATION + EXPERIENCE SEARCH STRATEGY (STRICT COUNTRY FILTER) ===');
  
  const { supabase } = await import("@/integrations/supabase/client");
  
  // Build base query with STRICT location filter
  let dbQuery = supabase
    .from('Complete feed')
    .select('*')
    .not('imageURL', 'is', null)
    .not('name', 'is', null);

  // Apply STRICT location filter - ONLY the specified country
  if (detectedLocation.type === 'country') {
    console.log('🎯 Applying STRICT country filter:', detectedLocation.info.country);
    console.log('🎯 DEBUG: Query should ONLY show results from:', detectedLocation.info.country);
    dbQuery = dbQuery.eq('country', detectedLocation.info.country);
  } else if (detectedLocation.type === 'region') {
    console.log('🎯 Applying STRICT region filter:', detectedLocation.info.region, 'in country:', detectedLocation.info.country);
    dbQuery = dbQuery
      .eq('country', detectedLocation.info.country)
      .or(`region.ilike.%${detectedLocation.info.region}%,city.ilike.%${detectedLocation.info.region}%`);
  } else if (detectedLocation.type === 'city') {
    console.log('🎯 Applying STRICT city filter:', detectedLocation.info.city, 'in country:', detectedLocation.info.country);
    dbQuery = dbQuery
      .eq('country', detectedLocation.info.country)
      .or(`city.ilike.%${detectedLocation.info.city}%,region.ilike.%${detectedLocation.info.city}%`);
  }

  // Apply experience/travel type filters for the SPECIFIC COUNTRY ONLY
  const experienceTerms = searchTerms.filter(term => 
    term.includes('rondreis') || term.includes('stedentrip') || term.includes('safari') || 
    term.includes('cruise') || term.includes('wintersport') || term.includes('wellness') ||
    term.includes('cultureel') || term.includes('natuur') || term.includes('avontuur')
  );

  if (experienceTerms.length > 0) {
    console.log('🏖️ Applying experience filters for SPECIFIC COUNTRY:', experienceTerms);
    console.log('🏖️ DEBUG: Building OR conditions for experience terms in', detectedLocation.info.country);
    
    const experienceConditions = experienceTerms.map(term => [
      `specialCategories.ilike.%${term}%`,
      `typeVakantie.ilike.%${term}%`,
      `categoryPath.ilike.%${term}%`,
      `description.ilike.%${term}%`,
      `name.ilike.%${term}%`
    ]).flat();

    dbQuery = dbQuery.or(experienceConditions.join(','));
  }

  // Apply additional filters if present
  if (priceConstraints) {
    const { applyPriceFilter } = await import('./filters/PriceFilter');
    dbQuery = applyPriceFilter(dbQuery, priceConstraints);
  }

  if (detectedFacilities) {
    const { applyFacilitiesFilter } = await import('./filters/FacilitiesFilter');
    dbQuery = applyFacilitiesFilter(dbQuery, detectedFacilities);
  }

  if (travelConstraints) {
    const { applyTravelDistanceFilter } = await import('./filters/TravelDistanceFilter');
    dbQuery = applyTravelDistanceFilter(dbQuery, travelConstraints);
  }

  // Execute query
  const { data, error } = await dbQuery.limit(100);
  
  if (error) {
    console.error('❌ Location + Experience search error:', error);
    throw error;
  }
  
  console.log(`🌍 STRICT Location + Experience search found ${data?.length || 0} results for ${detectedLocation.info.country}`);
  console.log('🔍 DEBUG: Sample of found results:');
  data?.slice(0, 3).forEach((result: any, index: number) => {
    console.log(`  ${index + 1}. ${result.name} - Country: ${result.country} - Categories: ${result.specialCategories}`);
  });
  
  const { verifyAndFilterResults } = await import('./utils/ResultsVerification');
  const finalResults = verifyAndFilterResults(data || [], detectedLocation, priceConstraints, detectedFacilities, query);
  
  console.log(`🎯 Final results after verification: ${finalResults.length} results`);
  return finalResults;
};

// Function to handle multiple location searches (e.g., "Kos of Kreta")
const handleMultipleLocationSearch = async (
  detectedMultipleLocations: DetectedMultipleLocations,
  priceConstraints: any,
  detectedFacilities: any,
  travelConstraints: any,
  query: string
) => {
  console.log('🌍 === MULTIPLE LOCATION SEARCH STRATEGY ===');
  console.log('Searching for multiple locations:', detectedMultipleLocations);
  
  const { supabase } = await import("@/integrations/supabase/client");
  
  // Build base query
  let dbQuery = supabase
    .from('Complete feed')
    .select('*')
    .not('imageURL', 'is', null)
    .not('name', 'is', null);

  // Apply multiple location filter
  const { applyLocationFilter } = await import('./filters/LocationFilter');
  dbQuery = applyLocationFilter(dbQuery, detectedMultipleLocations);

  // Apply additional filters if present
  if (priceConstraints) {
    const { applyPriceFilter } = await import('./filters/PriceFilter');
    dbQuery = applyPriceFilter(dbQuery, priceConstraints);
  }

  if (detectedFacilities) {
    const { applyFacilitiesFilter } = await import('./filters/FacilitiesFilter');
    dbQuery = applyFacilitiesFilter(dbQuery, detectedFacilities);
  }

  if (travelConstraints) {
    const { applyTravelDistanceFilter } = await import('./filters/TravelDistanceFilter');
    dbQuery = applyTravelDistanceFilter(dbQuery, travelConstraints);
  }

  // Execute query
  const { data, error } = await dbQuery.limit(100);
  
  if (error) {
    console.error('❌ Multiple location search error:', error);
    throw error;
  }
  
  console.log(`🌍 Multiple location search found ${data?.length || 0} results`);
  console.log('🔍 Sample of found results:');
  data?.slice(0, 5).forEach((result: any, index: number) => {
    console.log(`  ${index + 1}. ${result.name} - ${result.city}, ${result.region}, ${result.country}`);
  });
  
  // For multiple locations, use simplified validation
  const { validateVacationData } = await import('./utils/SearchUtils');
  const finalResults = validateVacationData(data || []);
  
  console.log(`🎯 Final results: ${finalResults.length} results`);
  return finalResults;
};
